/**
 * P14-A — Company Switcher (visual only).
 *
 * Phase A: shows the list of active companies and remembers the user's
 * pick in localStorage. NO backend scoping yet — this is purely UI
 * groundwork so the topbar shape stays stable when Phase C lights up
 * the actual JWT-based switch endpoint.
 *
 * Phase C will:
 *   1. POST /api/auth/switch-company on selection
 *   2. Replace the JWT in localStorage with the new signed token
 *   3. Reload the page to re-fetch all data under the new scope
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronDown, Star } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const STORAGE_KEY = 'active_company_id';

export const useActiveCompanyId = () => {
  const [value, setValue] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const set = useCallback((v) => {
    try {
      if (v) localStorage.setItem(STORAGE_KEY, v);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    setValue(v);
  }, []);
  return [value, set];
};

const CompanySwitcher = () => {
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveId] = useActiveCompanyId();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/companies');
        if (cancelled) return;
        const list = data.items || [];
        setCompanies(list);
        if (!activeId && list.length > 0) {
          const def = list.find((c) => c.is_default) || list[0];
          setActiveId(def.id);
        }
      } catch {
        /* Phase A: switcher silently hides if endpoint unavailable */
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, setActiveId]);

  // Click-outside
  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  const active = useMemo(
    () => companies.find((c) => c.id === activeId) || companies[0],
    [companies, activeId],
  );

  if (companies.length === 0) return null;

  return (
    <div ref={ref} className="relative" data-testid="company-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="company-switcher-button"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-xs"
      >
        <Building2 className="w-3.5 h-3.5 text-indigo-700" />
        <span className="font-semibold text-gray-900">{active?.code || '—'}</span>
        {active?.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-400" />}
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {open && (
        <div
          data-testid="company-switcher-menu"
          className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-400 border-b">
            Tenant
          </div>
          {companies.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                onClick={async () => {
                  if (isActive) { setOpen(false); return; }
                  try {
                    const { data } = await axiosInstance.post('/auth/switch-company',
                      { company_id: c.id });
                    if (data?.token) {
                      try { localStorage.setItem('token', data.token); } catch { /* ignore */ }
                    }
                    setActiveId(c.id);
                    setOpen(false);
                    toast.success(`Switched to ${c.code}`);
                    // Reload so every page re-fetches under the new scope.
                    setTimeout(() => window.location.reload(), 300);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Switch failed');
                  }
                }}
                data-testid={`switch-to-${c.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                  isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-indigo-600' : 'bg-gray-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{c.code}</div>
                  <div className="text-[10px] text-gray-500 truncate">{c.name}</div>
                </div>
                {c.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-400" />}
              </button>
            );
          })}
          <div className="px-3 py-2 border-t text-[10px] text-gray-400">
            Re-signs JWT · audit + scoped-db enforce isolation
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
